import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, Rocket, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import {
  CLIENT_FILTER_ALL,
  ClientFilterSelect,
  projectMatchesClientFilter,
} from "@/components/client-filter-select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { type ProjectSummary, queryKeys, useProjects } from "@/lib/api";

import { ProjectCard } from "./project-card";
import { ProjectDetailDialog } from "./project-detail-dialog";

// Fields matched by the homescreen search. Covers the project itself plus its
// creator (owner name/email) so colleagues can find a project by more than its
// name -- e.g. who created it or what it's for. "Client" is not a persisted
// field on a project, so it is not searchable here.
function projectMatchesQuery(project: ProjectSummary, query: string): boolean {
  const haystack = [
    project.project_name,
    project.project_slug,
    project.description,
    project.owner_name,
    project.owner_email,
    project.custom_subdomain,
    project.status,
  ];
  return haystack.some((field) => field?.toLowerCase().includes(query));
}

interface ProjectListProps {
  githubConnected: boolean;
  refreshTrigger?: number;
  newProjectButton?: React.ReactNode;
  projectId?: number;
  tab?: string;
  openProject: (id: number, tab?: string) => void;
  closeProject: () => void;
  setProjectTab: (tab: string) => void;
}

export function ProjectList({
  githubConnected,
  refreshTrigger,
  newProjectButton,
  projectId,
  tab,
  openProject,
  closeProject,
  setProjectTab,
}: ProjectListProps) {
  const queryClient = useQueryClient();
  const { data: projects = [], isLoading, refetch } = useProjects(githubConnected);
  // While GitHub isn't connected the query is disabled, so it never "loads".
  const loading = githubConnected && isLoading;
  const [clientFilter, setClientFilter] = useState<string>(CLIENT_FILTER_ALL);
  const [search, setSearch] = useState("");
  const trimmedSearch = search.trim().toLowerCase();

  const filteredProjects = useMemo(
    () =>
      projects
        .filter((p) => projectMatchesClientFilter(p, clientFilter))
        .filter((project) => !trimmedSearch || projectMatchesQuery(project, trimmedSearch)),
    [projects, clientFilter, trimmedSearch],
  );

  const fetchProjects = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Optimistically patch a project's resource URLs in the cache after a
  // resource is deleted, so the list reflects the change without a refetch.
  const handleResourceUpdated = useCallback(
    (pid: string | number, updates: Partial<ProjectSummary>) => {
      queryClient.setQueryData<ProjectSummary[]>(queryKeys.projects, (prev) =>
        prev ? prev.map((p) => (p.id === pid ? { ...p, ...updates } : p)) : prev,
      );
    },
    [queryClient],
  );

  const selectedProject = projects.find((p) => p.id === projectId) ?? null;
  const detailOpen = !!projectId && !!selectedProject;

  // Re-fetch when the parent bumps refreshTrigger (e.g. after project creation
  // or an out-of-sync resolution).
  useEffect(() => {
    if (githubConnected && refreshTrigger) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.projects });
    }
  }, [refreshTrigger, githubConnected, queryClient]);

  // Close dialog if projectId in URL doesn't match any loaded project
  useEffect(() => {
    if (!loading && projectId && projects.length > 0 && !projects.find((p) => p.id === projectId)) {
      closeProject();
    }
  }, [loading, projectId, projects, closeProject]);

  if (!githubConnected) {
    return (
      <p className="text-muted-foreground py-12 text-center text-sm">
        Connect your GitHub account above to start creating projects.
      </p>
    );
  }

  if (loading) {
    return <p className="text-muted-foreground py-12 text-center text-sm">Loading projects...</p>;
  }

  if (projects.length === 0) {
    return (
      <div className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="bg-accent/10 rounded-full p-4">
          <Rocket className="text-accent size-8" />
        </div>
        <div>
          <p className="text-primary text-lg font-bold">No projects yet</p>
          <p className="text-muted-foreground mt-1 text-sm">
            Create your first project or learn how Scaffold UI works
          </p>
        </div>
        <div className="flex gap-3">
          {newProjectButton}
          <Link to="/how-to">
            <Button variant="outline" size="sm">
              <BookOpen className="mr-2 size-4" />
              How to Get Started
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mb-4 flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, description, creator..."
            aria-label="Search projects"
            className="pl-9"
          />
        </div>
        <span className="text-muted-foreground text-sm">Filter by client</span>
        <ClientFilterSelect value={clientFilter} onChange={setClientFilter} className="w-44" />
      </div>

      {filteredProjects.length === 0 ? (
        <p className="text-muted-foreground py-12 text-center text-sm">
          {trimmedSearch ? (
            <>No projects match &ldquo;{search.trim()}&rdquo;.</>
          ) : (
            "No projects match this client filter."
          )}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onClick={() => openProject(project.id)}
            />
          ))}
        </div>
      )}

      <ProjectDetailDialog
        project={selectedProject}
        open={detailOpen}
        onOpenChange={(open) => {
          if (!open) {
            closeProject();
          }
        }}
        onProjectDeleted={() => {
          closeProject();
          fetchProjects();
        }}
        onResourceUpdated={handleResourceUpdated}
        tab={tab}
        onTabChange={setProjectTab}
      />
    </>
  );
}
